// TutorialApi.ts
import axios, { AxiosResponse, RawAxiosRequestHeaders } from 'axios';
import Cookies from 'universal-cookie';
import General from '../../../../general';
import config from '../../../../config';
import { ScreenHeadings, UiPropsInterface } from '../modular/types';

export interface UserAuth {
    userId: string;
    token: string;
}

export interface ApiHeaders {
    apiKey: string;
    userId?: string;
    token?: string;
}

export interface FeedbackWorkflowAction {
    actionId: string;
    actionType: string;
    answers?: string[] | [];
    campaignVariationId?: string;
    isCompleted?: boolean;
    isEnabled?: boolean;
    isRequired?: boolean;
    metadata?: {
        placeholder?: string;
        link?: string;
        manualInput?: string;
        regexKey?: string;
        regexErrorMessage?: string;
    },
    status: boolean;
    options?: string[] | [];
    title: string;
    manualInput?: string;
    // regexKey?: string;
    // regexMessage?: string;
}


interface OnboardingFormData {
    type: string;
    question: string;
    options: Array<string>;
    actionId: string;
    required: boolean;
    placeholder: string;
    linkTitle: string;
    linkUrl: string;
    manualInput: string | boolean;
    regexKey?: string | undefined;
    regexMessage?: string;

    // actionId?: string;
    // actionType?: string;
    // answers?: string[] | [];
    // isRequired?: boolean;
    // metadata?: {
    //     placeholder?: string;
    // };
    // options?: string[] | [];
    // title: string;
    // isCompleted: boolean;
}

interface childCampaignActions {
    actions?: FeedbackWorkflowAction[],
    campaignId?: string
    campaignVariationId?: string;
    title?: string;
}

export interface GeneralFeedbackContentStyleConfig {
    Form?: React.CSSProperties;
    Heading?: React.CSSProperties;
    Description?: React.CSSProperties;
    Input?: React.CSSProperties;
    Label?: React.CSSProperties;
    EmailError?: {
        text?: string,
        errorStyle?: React.CSSProperties
    },

    TopBar?: React.CSSProperties;
    TextArea?: React.CSSProperties;
    PrimaryButton?: React.CSSProperties;
    SecondaryButton?: React.CSSProperties;
    Modal?: React.CSSProperties;
    Footer?: React.CSSProperties;
    listHeading?: React.CSSProperties;
    listDescription?: React.CSSProperties;
    Card?: React.CSSProperties;
    Star?: {
        Style?: React.CSSProperties;
        PrimaryColor?: string;
        SecondaryColor?: string;
        Size?: number;
    }
    listHover?: {
        background?: string;
        iconBackground?: string;
        iconColor?: string;
        Heading?: string;
        Description?: string;
        IconSize?: string;
        Icon?: React.CSSProperties;
    };
    ThanksPopup?: {
        Style?: React.CSSProperties;
        Heading?: React.CSSProperties;
        Description?: React.CSSProperties;
        ShowFooter?: boolean;
        Icon?: React.CSSProperties;
    },
}

export interface QuestResponse {
    success: boolean;
    data: {
        childCampaignActions: childCampaignActions[];
        sdkConfig?: {
            uiProps?: UiPropsInterface;
        };
        campaignVariationId: string;
        metadata?: any;
    };
}

interface GetUserAuthParams {
    apiKey: string;
    entityId: string;
    uniqueUserId?: string;
    uniqueEmailId?: string;
    userId?: string;
    token?: string;
}

interface ExternalLoginResponse {

    userId: string;
    token: string;

}

interface VerifyActionResponse {
    success: boolean;
    error?: string;
    data?: any;
}

interface ExternalLoginResponse {
    userId: string;
    token: string;
}

interface ApiResponse<T> {
    success: boolean;
    data: T;
    error?: string;
    userId: string;
    token: string;
}

class FeedbackWorkflowApi {
    private BACKEND_URL: string;
    private GeneralFunctions: General;
    private cookies: Cookies;

    constructor(apiType: string) {
        this.BACKEND_URL = apiType === "STAGING" ? config.BACKEND_URL_STAGING : config.BACKEND_URL;
        this.GeneralFunctions = new General("mixpanel", apiType);
        this.cookies = new Cookies();
    }

    private getAxiosHeaders(headers: ApiHeaders): RawAxiosRequestHeaders {
        return {
            'Content-Type': 'application/json',
            ...headers
        };
    }

    async getUserAuth({
        apiKey,
        entityId,
        uniqueUserId,
        uniqueEmailId,
        userId,
        token,
    }: GetUserAuthParams): Promise<UserAuth | null> {
        try {

            const externalUserId = this.cookies.get("externalUserId");
            const questUserId = this.cookies.get("questUserId");
            const questUserToken = this.cookies.get("questUserToken");


            if (
                externalUserId &&
                questUserId &&
                questUserToken &&
                (externalUserId == uniqueUserId || externalUserId == uniqueEmailId)
            ) {
                return {
                    userId: questUserId,
                    token: questUserToken
                };
            }

            if (uniqueUserId) {
                const headers = this.getAxiosHeaders({
                    apiKey,
                    ...(token && { token }),
                    ...(userId && { userId })
                });

                const body = {
                    externalUserId: uniqueUserId,
                    entityId,
                    email: uniqueEmailId || null,
                    ignoreMAU: true,
                };

                const response = await axios.post<ApiResponse<ExternalLoginResponse>>(
                    `${this.BACKEND_URL}api/users/external/login`,
                    body,
                    { headers }
                );


                if (!response.data?.success) {
                    throw new Error('Invalid response from external login');
                }

                const { userId: newUserId, token: newToken } = response.data;
                const expires = new Date();
                expires.setHours(expires.getHours() + 12);

                this.cookies.set("externalUserId", uniqueUserId, { path: "/", expires });
                this.cookies.set("questUserId", newUserId, { path: "/", expires });
                this.cookies.set("questUserToken", newToken, { path: "/", expires });

                return {
                    userId: newUserId,
                    token: newToken
                };
            }

            // If no uniqueUserId but we have userId and token, use those
            if (userId && token) {
                return { userId, token };
            }

            return null;
        } catch (error) {
            this.GeneralFunctions.captureSentryException(error);
            return null;
        }
    }

    async fetchCampaignData(
        entityId: string,
        questId: string,
        headers: ApiHeaders,
        variation?: string
    ): Promise<QuestResponse['data']> { // Note the return type change
        try {
            if (!headers.userId || !headers.token) {
                throw new Error('Missing required headers');
            }

            const params = new URLSearchParams();
            params.set("platform", "REACT");
            if (variation) params.set("variation", variation);

            const axiosHeaders = this.getAxiosHeaders(headers);
            // const request = `${this.BACKEND_URL}api/v2/entities/${entityId}/campaigns/${questId}?${params.toString()}`;
            const request = `${this.BACKEND_URL}api/v2/entities/${entityId}/campaigns/${questId}/parent?${params.toString()}`;

            const response = await axios.get<QuestResponse>(request, {
                headers: axiosHeaders
            });

            if (!response.data?.data) {
                throw new Error('No data received from API');
            }

            return response.data.data;
        } catch (error) {
            this.GeneralFunctions.captureSentryException(error);
            throw error;
        }
    }

    async verifyAction(
        entityId: string,
        questId: string,
        headers: ApiHeaders,
        actionId: string,
        campaignVariationId: string
    ): Promise<VerifyActionResponse> {
        try {
            if (!headers.userId || !headers.token) {
                throw new Error('Missing required headers');
            }

            const body = {
                actions: [
                    {
                        answers: [],
                        actionId,
                    },
                ],
                campaignVariationId,
            };

            const axiosHeaders = this.getAxiosHeaders(headers);
            const request = `${this.BACKEND_URL}api/v2/entities/${entityId}/campaigns/${questId}/verify`;

            const response = await axios.post<ApiResponse<VerifyActionResponse>>(
                request,
                body,
                { headers: axiosHeaders }
            );

            if (!response.data?.data) {
                throw new Error('Invalid response from verify action');
            }

            return response.data.data;
        } catch (error) {
            this.GeneralFunctions.captureSentryException(error);
            throw error;
        }
    }

    clearCookies(): void {
        this.cookies.remove("externalUserId", { path: "/" });
        this.cookies.remove("questUserId", { path: "/" });
        this.cookies.remove("questUserToken", { path: "/" });
    }

    isValidUserAuth(userAuth: unknown): userAuth is UserAuth {
        return (
            !!userAuth &&
            typeof (userAuth as UserAuth).userId === 'string' &&
            typeof (userAuth as UserAuth).token === 'string'
        );
    }



    metricApi = (headers: any, BACKEND_URL: string | undefined, entityId: string, campaignId: string | undefined, campaignVariationId: string, body?: { [key: string]: any },) => {
        const api = async (page: string) => {
            try {
                const url = `${BACKEND_URL}api/entities/${entityId}/metrics/${page}/campaign`;
                const response = await axios.post(url, { campaignId, campaignVariationId, ...body }, { headers });

                if (response.status === 200 && response.data.success) {
                    return response;
                }
                // else {
                //   throw new Error(response.statusText);
                // }
            } catch (error: any) {
                if (error.response && error.response.data && error.response.data.error) {
                    console.error('API request failed:', error.response.data.error);
                } else {
                    console.error('API request failed:', error.message);
                }
                // throw error;
            }
        };

        return {
            async view() {
                return api('onboarding-view');
            },
            async page(pageNumber: number) {
                return api(`onboarding-complete-page-${pageNumber}`);
            },
            async complete() {
                return api('onboarding-complete');
            }
        };
    };
}

export default FeedbackWorkflowApi;